/* @ts-nocheck */
/* global google */
import React, { useEffect, useMemo, useState, useCallback, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { GoogleMap, useLoadScript } from '@react-google-maps/api';
import { GoogleMapsOverlay } from '@deck.gl/google-maps';
import { PathLayer, TextLayer } from '@deck.gl/layers';
import { supabase } from '../../utils/supabaseClient';
import {
  MapIcon,
  ArrowsPointingOutIcon,
  ListBulletIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';

const LIBRARIES = ['geometry'];
const mapContainerStyle = { width: '100%', height: '100%' };

// Stable literals so @react-google-maps/api doesn't re-apply setCenter/setZoom
// on every render. Real initial viewport is set via fitBounds in onMapLoad.
const INITIAL_CENTER = { lat: 39.5, lng: -98.35 };
const INITIAL_ZOOM = 4;

// Same palette as the home map. Hashed by folder_id so each folder gets a
// distinct color within a project. Unfoldered sessions fall back to the
// first slot (muted).
const FOLDER_PALETTE = [
  { stroke: [14, 165, 233],  hex: '#0ea5e9' },
  { stroke: [236, 72, 153],  hex: '#ec4899' },
  { stroke: [132, 204, 22],  hex: '#84cc16' },
  { stroke: [249, 115, 22],  hex: '#f97316' },
  { stroke: [217, 70, 239],  hex: '#d946ef' },
  { stroke: [6, 182, 212],   hex: '#06b6d4' },
  { stroke: [168, 85, 247],  hex: '#a855f7' },
  { stroke: [225, 29, 72],   hex: '#e11d48' },
  { stroke: [20, 184, 166],  hex: '#14b8a6' },
  { stroke: [245, 158, 11],  hex: '#f59e0b' },
];
const UNFOLDERED_COLOR = { stroke: [107, 114, 128], hex: '#6b7280' }; // gray

function colorForFolder(folderId) {
  if (!folderId) return UNFOLDERED_COLOR;
  let hash = 0;
  for (let i = 0; i < folderId.length; i++) {
    hash = ((hash << 5) - hash) + folderId.charCodeAt(i);
    hash |= 0;
  }
  return FOLDER_PALETTE[Math.abs(hash) % FOLDER_PALETTE.length];
}

function formatRelativeDate(dateStr) {
  if (!dateStr) return 'No date';
  const ageDays = Math.floor((Date.now() - new Date(dateStr).getTime()) / 86400000);
  if (ageDays === 0) return 'Today';
  if (ageDays === 1) return 'Yesterday';
  if (ageDays < 7) return `${ageDays}d ago`;
  if (ageDays < 30) return `${Math.floor(ageDays / 7)}w ago`;
  if (ageDays < 365) return `${Math.floor(ageDays / 30)}mo ago`;
  return `${Math.floor(ageDays / 365)}y ago`;
}

function geojsonToPaths(g) {
  if (!g) return [];
  if (g.type === 'LineString') return [g.coordinates];
  if (g.type === 'MultiLineString') return g.coordinates;
  return [];
}

const RANGE_OPTIONS = [
  { key: 'all', label: 'All',  days: null },
  { key: '1y',  label: '1y',   days: 365 },
  { key: '90d', label: '90d',  days: 90 },
  { key: '30d', label: '30d',  days: 30 },
];

function rangeCutoff(rangeKey) {
  const opt = RANGE_OPTIONS.find(r => r.key === rangeKey);
  if (!opt || opt.days == null) return null;
  return Date.now() - opt.days * 86400000;
}

export default function ProjectMapView({ projectId }) {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [hoveredSession, setHoveredSession] = useState(null);
  const [mapStyleMode, setMapStyleMode] = useState('roadmap');
  const [mapReady, setMapReady] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(true);
  const mapRef = useRef(null);
  const deckOverlayRef = useRef(null);

  // Date range lives in URL so refresh / tab-nav preserves it.
  const rangeKey = searchParams.get('range') || 'all';
  const validRangeKey = RANGE_OPTIONS.some(r => r.key === rangeKey) ? rangeKey : 'all';

  const handleRangeChange = useCallback((key) => {
    const params = new URLSearchParams(searchParams);
    if (key === 'all') params.delete('range');
    else params.set('range', key);
    setSearchParams(params, { replace: true });
  }, [searchParams, setSearchParams]);

  const mapOptions = useMemo(() => ({
    disableDefaultUI: true,
    keyboardShortcuts: false,
    clickableIcons: false,
    mapTypeId: mapStyleMode === 'satellite' ? 'satellite' : 'roadmap',
    minZoom: 3,
    gestureHandling: 'greedy',
    zoomControl: false,
    mapTypeControl: false,
    scaleControl: false,
    streetViewControl: false,
    rotateControl: false,
    fullscreenControl: false,
  }), [mapStyleMode]);

  const { isLoaded, loadError } = useLoadScript({
    googleMapsApiKey: import.meta.env.VITE_GOOGLE_MAPS_API_KEY,
    libraries: LIBRARIES,
  });

  useEffect(() => {
    if (!projectId) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      const { data, error: rpcError } = await supabase
        .rpc('get_project_sessions_coverage', { project_id_input: projectId });
      if (cancelled) return;
      if (rpcError) {
        setError(rpcError.message);
        setLoading(false);
        return;
      }
      setSessions(data || []);
      setLoading(false);
    })();
    return () => { cancelled = true; };
  }, [projectId]);

  const filteredSessions = useMemo(() => {
    const cutoff = rangeCutoff(validRangeKey);
    if (cutoff == null) return sessions;
    return sessions.filter(s => s.run_date && new Date(s.run_date).getTime() >= cutoff);
  }, [sessions, validRangeKey]);

  // Newest run_date first; sessions without run_date sort to the bottom.
  const sortedSessions = useMemo(() => {
    return [...filteredSessions].sort((a, b) => {
      const ta = a.run_date ? new Date(a.run_date).getTime() : -Infinity;
      const tb = b.run_date ? new Date(b.run_date).getTime() : -Infinity;
      return tb - ta;
    });
  }, [filteredSessions]);

  // Scroll the hovered drawer row into view (covers map-originated hovers).
  const rowRefs = useRef(new Map());
  const setRowRef = useCallback((id) => (el) => {
    if (el) rowRefs.current.set(id, el);
    else rowRefs.current.delete(id);
  }, []);
  useEffect(() => {
    if (!hoveredSession) return;
    const el = rowRefs.current.get(hoveredSession);
    if (el) el.scrollIntoView({ block: 'nearest' });
  }, [hoveredSession]);

  const pathRecords = useMemo(() => {
    const out = [];
    filteredSessions.forEach(s => {
      const color = colorForFolder(s.folder_id);
      geojsonToPaths(s.path_geojson).forEach(path => {
        out.push({
          session_id: s.session_id,
          session_name: s.session_name,
          folder_id: s.folder_id,
          folder_name: s.folder_name,
          run_date: s.run_date,
          colorStroke: color.stroke,
          hex: color.hex,
          path,
        });
      });
    });
    return out;
  }, [filteredSessions]);

  const bounds = useMemo(() => {
    if (filteredSessions.length === 0) return null;
    let minLat = Infinity, maxLat = -Infinity, minLng = Infinity, maxLng = -Infinity;
    filteredSessions.forEach(s => {
      if (s.min_lat != null && s.min_lat < minLat) minLat = s.min_lat;
      if (s.max_lat != null && s.max_lat > maxLat) maxLat = s.max_lat;
      if (s.min_lng != null && s.min_lng < minLng) minLng = s.min_lng;
      if (s.max_lng != null && s.max_lng > maxLng) maxLng = s.max_lng;
    });
    if (minLat === Infinity) return null;
    return { minLat, maxLat, minLng, maxLng };
  }, [filteredSessions]);

  const handleSessionClick = useCallback((sessionId) => {
    if (!sessionId) return;
    navigate(`/session/${sessionId}`);
  }, [navigate]);

  const fitAllBounds = useCallback(() => {
    if (!mapRef.current || !bounds) return;
    const b = new google.maps.LatLngBounds(
      { lat: bounds.minLat, lng: bounds.minLng },
      { lat: bounds.maxLat, lng: bounds.maxLng }
    );
    mapRef.current.fitBounds(b, 60);
  }, [bounds]);

  const onMapLoad = useCallback((map) => {
    mapRef.current = map;

    const overlay = new GoogleMapsOverlay({
      layers: [],
      interleaved: false,
      useDevicePixels: true,
    });
    overlay.setMap(map);
    deckOverlayRef.current = overlay;
    setMapReady(true);

    if (bounds) {
      const b = new google.maps.LatLngBounds(
        { lat: bounds.minLat, lng: bounds.minLng },
        { lat: bounds.maxLat, lng: bounds.maxLng }
      );
      map.fitBounds(b, 60);
    }
  }, [bounds]);

  const onMapUnmount = useCallback(() => {
    if (deckOverlayRef.current) {
      deckOverlayRef.current.setMap(null);
      deckOverlayRef.current.finalize();
      deckOverlayRef.current = null;
    }
    mapRef.current = null;
    setMapReady(false);
  }, []);

  // Bbox center label position for the hovered session. Stable across renders
  // (anchored to data, not cursor) so the label doesn't jitter.
  const hoveredLabel = useMemo(() => {
    if (!hoveredSession) return null;
    const s = sessions.find(row => row.session_id === hoveredSession);
    if (!s || s.min_lat == null || s.max_lat == null || s.min_lng == null || s.max_lng == null) return null;
    return {
      session_id: s.session_id,
      text: s.session_name || 'Unnamed Session',
      position: [(s.min_lng + s.max_lng) / 2, (s.min_lat + s.max_lat) / 2],
    };
  }, [hoveredSession, sessions]);

  useEffect(() => {
    if (!deckOverlayRef.current) return;

    const layers = [
      new PathLayer({
        id: 'session-paths',
        data: pathRecords,
        getPath: d => d.path,
        getColor: d => {
          const isHovered = hoveredSession === d.session_id;
          return [...d.colorStroke, isHovered ? 255 : 220];
        },
        getWidth: d => (hoveredSession === d.session_id ? 6 : 3),
        widthUnits: 'pixels',
        capRounded: true,
        jointRounded: true,
        pickable: true,
        onHover: info => setHoveredSession(info?.object?.session_id ?? null),
        onClick: info => handleSessionClick(info?.object?.session_id),
        updateTriggers: {
          getColor: hoveredSession,
          getWidth: hoveredSession,
        },
      }),
    ];

    if (hoveredLabel) {
      layers.push(new TextLayer({
        id: 'hovered-session-label',
        data: [hoveredLabel],
        getPosition: d => d.position,
        getText: d => d.text,
        getSize: 13,
        getColor: [17, 24, 39, 255], // gray-900
        getPixelOffset: [0, -14],
        background: true,
        getBackgroundColor: [255, 255, 255, 240],
        getBorderColor: [209, 213, 219, 255], // gray-300
        getBorderWidth: 1,
        backgroundPadding: [8, 4],
        backgroundBorderRadius: 6,
        billboard: true,
        sizeUnits: 'pixels',
        pickable: false,
        parameters: { depthTest: false },
      }));
    }

    deckOverlayRef.current.setProps({ layers });
  }, [pathRecords, hoveredSession, hoveredLabel, handleSessionClick, mapReady]);

  const showMap = isLoaded && !loading && !error;

  if (loadError) {
    return (
      <div className="h-full flex flex-col bg-white">
        <div className="flex-1 relative">
          <ErrorState message="Failed to load Google Maps" />
        </div>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col bg-white">
      {/* Toolbar — date filter on the left, drawer toggle on the right */}
      <div className="flex-shrink-0 h-14 border-b border-gray-200 px-6 flex items-center justify-between bg-gray-50">
        <div className="flex items-center gap-3">
          <DateRangePills value={validRangeKey} onChange={handleRangeChange} />
          <p className="text-xs text-gray-500">
            {loading
              ? 'Loading…'
              : `${filteredSessions.length} session${filteredSessions.length === 1 ? '' : 's'}`}
          </p>
        </div>
        <button
          onClick={() => setDrawerOpen(o => !o)}
          title={drawerOpen ? 'Hide sessions' : 'Show sessions'}
          aria-pressed={drawerOpen}
          className={`inline-flex items-center gap-1.5 h-8 px-3 text-xs font-medium rounded border transition-colors ${
            drawerOpen
              ? 'bg-gray-100 text-gray-900 border-gray-300'
              : 'bg-white text-gray-600 border-gray-300 hover:bg-gray-100'
          }`}
        >
          {drawerOpen ? <XMarkIcon className="h-4 w-4" /> : <ListBulletIcon className="h-4 w-4" />}
          Sessions
        </button>
      </div>

      <div className="flex-1 flex min-h-0">
        <div className="flex-1 relative min-w-0">
          {error && <ErrorState message={error} />}
          {!error && (!isLoaded || loading) && (
            <LoadingOverlay label={isLoaded ? 'Loading sessions…' : 'Loading map…'} />
          )}
          {showMap && sessions.length === 0 && <EmptyState />}
          {showMap && sessions.length > 0 && filteredSessions.length === 0 && <NoMatchState />}

          {showMap && (
            <GoogleMap
              mapContainerStyle={mapContainerStyle}
              options={mapOptions}
              onLoad={onMapLoad}
              onUnmount={onMapUnmount}
              center={INITIAL_CENTER}
              zoom={INITIAL_ZOOM}
            />
          )}

          {showMap && (
            <MapControls
              mode={mapStyleMode}
              onChange={setMapStyleMode}
              onFitAll={fitAllBounds}
              canFit={!!bounds}
            />
          )}

        </div>

        {showMap && (
          <SessionDrawer
            open={drawerOpen}
            sessions={sortedSessions}
            hoveredSession={hoveredSession}
            onHover={setHoveredSession}
            onSelect={handleSessionClick}
            setRowRef={setRowRef}
          />
        )}
      </div>
    </div>
  );
}

// ---------- Sub-components ----------

function MapControls({ mode, onChange, onFitAll, canFit }) {
  return (
    <div className="absolute top-3 left-3 z-[1001]" onClick={(e) => e.stopPropagation()}>
      <div className="flex items-center space-x-1 p-1 bg-white border border-gray-200 rounded-lg shadow-lg">
        <button
          onClick={() => onChange('satellite')}
          className={`px-2.5 py-1.5 text-xs font-semibold rounded transition-colors ${
            mode === 'satellite' ? 'bg-red-600 text-white' : 'text-gray-600 hover:bg-gray-100'
          }`}
        >
          Satellite
        </button>
        <button
          onClick={() => onChange('roadmap')}
          className={`px-2.5 py-1.5 text-xs font-semibold rounded transition-colors ${
            mode === 'roadmap' ? 'bg-red-600 text-white' : 'text-gray-600 hover:bg-gray-100'
          }`}
        >
          Map
        </button>
        <span className="w-px h-5 bg-gray-200 mx-0.5" />
        <button
          onClick={onFitAll}
          disabled={!canFit}
          title="Fit all visible sessions"
          className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-semibold rounded text-gray-600 hover:bg-gray-100 transition-colors disabled:opacity-40 disabled:hover:bg-transparent disabled:cursor-not-allowed"
        >
          <ArrowsPointingOutIcon className="h-3.5 w-3.5" />
          Fit
        </button>
      </div>
    </div>
  );
}

function DateRangePills({ value, onChange }) {
  return (
    <div className="inline-flex rounded border border-gray-300 divide-x divide-gray-300">
      {RANGE_OPTIONS.map((r, i) => {
        const active = value === r.key;
        const rounding = i === 0
          ? 'rounded-l'
          : i === RANGE_OPTIONS.length - 1
            ? 'rounded-r'
            : '';
        return (
          <button
            key={r.key}
            onClick={() => onChange(r.key)}
            className={`px-3 py-1 text-sm font-medium transition-colors ${rounding} ${
              active
                ? 'bg-gray-100 text-gray-900'
                : 'text-gray-600 hover:bg-gray-100'
            }`}
          >
            {r.label}
          </button>
        );
      })}
    </div>
  );
}

function LoadingOverlay({ label }) {
  return (
    <div className="absolute inset-0 flex items-center justify-center bg-white z-10">
      <div className="text-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-red-600 mx-auto mb-3" />
        <p className="text-sm text-gray-600">{label}</p>
      </div>
    </div>
  );
}

function EmptyState() {
  return (
    <div className="absolute inset-0 flex items-center justify-center z-10 pointer-events-none">
      <div className="bg-white/90 border border-gray-200 rounded-lg px-6 py-5 text-center max-w-sm">
        <MapIcon className="h-10 w-10 text-gray-300 mx-auto mb-2" />
        <h3 className="text-sm font-semibold text-gray-900 mb-1">No coverage yet</h3>
        <p className="text-xs text-gray-500">
          Sessions will appear here once they're uploaded and processed.
        </p>
      </div>
    </div>
  );
}

function NoMatchState() {
  return (
    <div className="absolute inset-0 flex items-center justify-center z-10 pointer-events-none">
      <div className="bg-white/90 border border-gray-200 rounded-lg px-6 py-5 text-center max-w-sm">
        <MapIcon className="h-10 w-10 text-gray-300 mx-auto mb-2" />
        <h3 className="text-sm font-semibold text-gray-900 mb-1">No sessions match</h3>
        <p className="text-xs text-gray-500">Try a wider date range.</p>
      </div>
    </div>
  );
}

function ErrorState({ message }) {
  return (
    <div className="absolute inset-0 flex items-center justify-center z-10">
      <div className="bg-red-50 border border-red-200 rounded-lg px-6 py-5 text-center max-w-sm">
        <h3 className="text-sm font-semibold text-red-800 mb-1">Couldn't load map</h3>
        <p className="text-xs text-red-700">{message}</p>
      </div>
    </div>
  );
}

function SessionDrawer({ open, sessions, hoveredSession, onHover, onSelect, setRowRef }) {
  return (
    <div
      className={`flex-shrink-0 border-l border-gray-200 bg-white overflow-hidden transition-[width] duration-200 ease-out ${
        open ? 'w-80' : 'w-0'
      }`}
    >
      {open && (
        <div className="h-full w-80 flex flex-col overflow-hidden">
          <div className="flex-shrink-0 px-4 py-3 border-b border-gray-200">
            <h3 className="text-sm font-semibold text-gray-900">Sessions</h3>
            <p className="text-xs text-gray-500">
              {sessions.length} run{sessions.length === 1 ? '' : 's'} · newest first
            </p>
          </div>
          <div className="flex-1 overflow-y-auto" onMouseLeave={() => onHover(null)}>
            {sessions.length === 0 ? (
              <div className="px-4 py-6 text-xs text-gray-500">No sessions in this range.</div>
            ) : (
              <ul className="divide-y divide-gray-100">
                {sessions.map(s => {
                  const color = colorForFolder(s.folder_id);
                  const isHovered = hoveredSession === s.session_id;
                  return (
                    <li
                      key={s.session_id}
                      ref={setRowRef(s.session_id)}
                      onMouseEnter={() => onHover(s.session_id)}
                      onClick={() => onSelect(s.session_id)}
                      className={`px-4 py-2.5 cursor-pointer transition-colors ${
                        isHovered ? 'bg-blue-50' : 'hover:bg-gray-50'
                      }`}
                    >
                      <div className="flex items-center gap-2">
                        <span
                          className="w-2.5 h-2.5 rounded-full flex-shrink-0"
                          style={{ backgroundColor: color.hex }}
                        />
                        <span className="text-sm text-gray-900 truncate flex-1">
                          {s.session_name || 'Unnamed Session'}
                        </span>
                      </div>
                      <div className="mt-0.5 ml-[18px] text-xs text-gray-500 truncate">
                        {s.folder_name || 'No folder'} · {formatRelativeDate(s.run_date)}
                      </div>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
